import React from "react";
import ReactDOM from "react-dom";
import { Button } from "../Button";
import colors from "../../colors/colors";
import { getPortalContainer } from "../../dev/ThemeManager/utils";

interface ConfirmDialogProps {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  dismissLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
  onDismiss?: () => void;
  isLoading?: boolean;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  title,
  message,
  confirmLabel = "Save",
  cancelLabel = "Discard",
  dismissLabel = "Cancel",
  onConfirm,
  onCancel,
  onDismiss,
  isLoading = false,
}) => {
  const dialogContent = (
    <>
      <style>{`
        .confirm-dialog-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000010;
        }

        .confirm-dialog-container {
          background: ${colors.white};
          border-radius: 12px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
          max-width: 400px;
          width: 90%;
          padding: 24px;
        }

        .confirm-dialog-title {
          margin: 0 0 12px;
          font-size: 18px;
          font-weight: 600;
          color: ${colors.primary};
        }

        .confirm-dialog-message {
          margin: 0 0 24px;
          font-size: 14px;
          color: ${colors.gray_medium};
          line-height: 1.5;
        }

        .confirm-dialog-actions {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .confirm-dialog-actions-right {
          display: flex;
          gap: 12px;
        }

        /* Reset client-site global CSS that applies min-width to buttons */
        .confirm-dialog-container button {
          min-width: 0;
        }
      `}</style>
      <div className="confirm-dialog-overlay" onClick={onCancel}>
        <div
          className="confirm-dialog-container"
          onClick={(e) => e.stopPropagation()}
        >
          <h3 className="confirm-dialog-title">{title}</h3>
          <p className="confirm-dialog-message">{message}</p>
          <div className="confirm-dialog-actions">
            <div>
              {onDismiss && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onDismiss}
                  disabled={isLoading}
                >
                  {dismissLabel}
                </Button>
              )}
            </div>
            <div className="confirm-dialog-actions-right">
              <Button
                variant="ghost"
                size="sm"
                onClick={onCancel}
                disabled={isLoading}
              >
                {cancelLabel}
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={onConfirm}
                isLoading={isLoading}
                disabled={isLoading}
              >
                {confirmLabel}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  // Portal to parent document when in iframe to escape mobile frame constraints
  return ReactDOM.createPortal(dialogContent, getPortalContainer());
};
